import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import {
  BulkImportPreview,
  BulkImportStatus,
  getBuildingBulkImportStatus,
  startBuildingBulkImport,
  validateBuildingBulkImport
} from '~/api/building';

const POLL_INTERVAL_MS = 2000;

// Import returns before the first job starts, so early polls can read inactive.
const STARTUP_GRACE_MS = 30000;

export default function useBuildingBulkImport() {
  const [file, setFileState] = useState<File | null>(null);
  const [preview, setPreview] = useState<BulkImportPreview | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [status, setStatus] = useState<BulkImportStatus | null>(null);
  // Latest import whether shown or not, for the "Download last import results" button.
  const [lastImport, setLastImport] = useState<BulkImportStatus | null>(null);

  // Set by the polling effect so startImport can restart a loop that already stopped.
  const wakePollRef = useRef<() => void>(() => {});

  // Polls on mount so a running import reappears. A finished one shows only if started here.
  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    let awaitingStartUntil = 0;
    let lastSeenActive = false;
    let startedHere = false;

    const poll = async () => {
      let keepPolling = false;
      try {
        const { data } = await getBuildingBulkImportStatus();
        if (cancelled) {
          return;
        }
        // lastSeenActive: take the finished state of an import already on screen.
        if (data.active || startedHere || lastSeenActive) {
          setStatus(data);
        }
        if (data.queueUuid) {
          setLastImport(data);
        }
        lastSeenActive = data.active;
        keepPolling = data.active || Date.now() < awaitingStartUntil;
      } catch (error) {
        // The batch keeps running server-side. The next tick picks it back up.
        keepPolling = lastSeenActive || Date.now() < awaitingStartUntil;
      }

      // Chained timeout rather than setInterval, so a slow response can't stack requests.
      if (!cancelled && keepPolling) {
        timer = setTimeout(poll, POLL_INTERVAL_MS);
      }
    };

    wakePollRef.current = () => {
      if (cancelled) {
        return;
      }
      awaitingStartUntil = Date.now() + STARTUP_GRACE_MS;
      startedHere = true;
      clearTimeout(timer);
      poll();
    };

    poll();

    return () => {
      wakePollRef.current = () => {};
      cancelled = true;
      clearTimeout(timer);
    };
  }, []);

  // A new file needs a new Validate before Import unlocks.
  const setFile = (next: File | null) => {
    setFileState(next);
    setPreview(null);
  };

  const validate = async () => {
    if (!file) {
      return;
    }
    setIsValidating(true);
    try {
      const { data } = await validateBuildingBulkImport(file);
      setPreview(data);
    } catch (error: any) {
      setPreview(null);
      // 400/500 are already toasted by the API client.
      if (error?.status !== 400 && error?.status !== 500) {
        toast.error(error?.message || 'Could not validate the CSV');
      }
    } finally {
      setIsValidating(false);
    }
  };

  const startImport = async () => {
    if (!file || !preview?.canImport) {
      return;
    }
    setIsStarting(true);
    try {
      const { data } = await startBuildingBulkImport(file);
      setStatus({
        queueUuid: data.queueUuid,
        active: true,
        abandoned: false,
        total: data.jobCount,
        completed: 0,
        failed: 0,
        rows: []
      });
      setFileState(null);
      setPreview(null);
      wakePollRef.current();
      toast.success(
        `Importing ${data.jobCount} building${
          data.jobCount === 1 ? '' : 's'
        } in the background`
      );
    } catch (error: any) {
      if (error?.status !== 400 && error?.status !== 500) {
        toast.error(error?.message || 'Could not start the import');
      }
    } finally {
      setIsStarting(false);
    }
  };

  return {
    file,
    setFile,
    preview,
    validate,
    isValidating,
    startImport,
    isStarting,
    canImport: !!preview?.canImport && !isStarting && !status?.active,
    status,
    lastImport
  };
}
